import { useCallback } from 'react';

import PromotionFillIcon from '@/components/Icons/PromotionFill';
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from '@/components/NavigationMenu';
import { Experiences } from '@/components/NewHeader/NavBar/Experiences';
import { Guides } from '@/components/NewHeader/NavBar/Guides';
import { Offers } from '@/components/NewHeader/NavBar/Offers';
import { VillasInPuglia } from '@/components/NewHeader/NavBar/VillasInPuglia';
import { VillasInSicily } from '@/components/NewHeader/NavBar/VillasInSicily';
import { useHeaderScroll } from '@/components/NewHeader/useHeaderScroll';
import { useTranslation } from '@/contexts/dictionaryContext';
import useGTMEvents from '@/hooks/useGTMEvents';
import { cn } from '@/lib/utils';
import Link from 'next/link';

type NavItem = {
  id: string;
  Content: React.ComponentType;
};

const navItem: NavItem[] = [
  {
    id: 'offers',
    Content: Offers,
  },
  {
    id: 'villas-in-sicily',
    Content: VillasInSicily,
  },
  {
    id: 'villas-in-puglia',
    Content: VillasInPuglia,
  },
  {
    id: 'experiences',
    Content: Experiences,
  },
  {
    id: 'guides',
    Content: Guides,
  },
];

export function NavBar() {
  const { selectedNavItem, setSelectedNavItem } = useHeaderScroll();
  const {
    newHeader: { nav },
  } = useTranslation();

  const { sendMenuClickEvent } = useGTMEvents();

  const renderNav = useCallback(() => {
    return nav.map((item) => {
      if (item.href) {
        return (
          <NavigationMenuItem
            onClick={() =>
              sendMenuClickEvent({ section: item.title, sectionItem: 'none' })
            }
            key={item.title}
            value={item.id}>
            <MainItem href={item.href} title={item.title} />
          </NavigationMenuItem>
        );
      } else {
        const Content = navItem.find((i) => i.id === item.id)?.Content!;
        return (
          <NavigationMenuItem
            onClick={() =>
              sendMenuClickEvent({ section: item.title, sectionItem: 'none' })
            }
            key={item.title}
            value={item.id}>
            <NavigationMenuTrigger
              className={cn(
                'py-6 text-sm',
                item.id === 'offers' && 'inline-flex items-center gap-1.5',
                selectedNavItem === item.id && 'border-[#cedbec]',
              )}>
              {item.id === 'offers' && (
                <PromotionFillIcon className="size-4 text-primary" />
              )}
              {item.title}
            </NavigationMenuTrigger>
            <Content />
          </NavigationMenuItem>
        );
      }
    });
  }, [nav, selectedNavItem, sendMenuClickEvent]);

  return (
    <NavigationMenu
      className="max-w-none hidden lg:block"
      value={selectedNavItem}
      onValueChange={setSelectedNavItem}>
      <div className="container 3xl:max-w-screen-2xl mx-auto">
        <NavigationMenuList className="justify-between">
          {renderNav()}
        </NavigationMenuList>
      </div>
    </NavigationMenu>
  );
}

function MainItem({ href, title }: { href: string; title: string }) {
  return (
    <NavigationMenuLink
      asChild
      className={cn(navigationMenuTriggerStyle(), 'text-sm text-inherit py-6')}>
      <Link href={href}>{title}</Link>
    </NavigationMenuLink>
  );
}
